import _ from 'lodash'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { PaginationState, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Alert, AppBar, Box, Dialog, IconButton, Skeleton, Slide, Stack, Toolbar, Tooltip } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import CloseIcon from '@mui/icons-material/Close'

import Pagination from '@/components/ui/pagination/Pagination'

import { useGetNetworkMembersQuery } from '@/core/services/network'
import { useLazyGetScheduleAvailabilitiesQuery } from '@/core/services/schedule'
import { NetworkMember } from '@/core/models/network.model'
import { Slot, WorkingHours } from '@/core/models/schedule.model'

import StaffAvailability, { StaffAvailabilityProps } from '@/pages/create-consultation/components/StaffAvailability'
import { CreateConsultationStepEnum, CreateConsultationStepper, CreateConsultationStepperValues } from '@/pages/create-consultation/CreateConsultationStepper'

dayjs.extend(isoWeek)

const rowsPerPageOptions = [5, 10, 15]

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" mountOnEnter unmountOnExit ref={ref} {...props} />
})

export default function MemberSchedulesTableContainer() {
  const { networkId } = useParams()
  const { t } = useTranslation()

  const { data } = useGetNetworkMembersQuery(networkId ?? skipToken, { refetchOnMountOrArgChange: true })
  const [getStaffAvailabilities, { isLoading: isLoadingAvailabilities }] = useLazyGetScheduleAvailabilitiesQuery()

  const [staffAvailabilities, setStaffAvailabilities] = useState<Record<string, { busySlots: Slot[]; workingHours: WorkingHours[]; timeZone?: string }>>({})
  const [openDialog, setOpenDialog] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPageOptions[0],
  })
  const [createConsultationInitialValues, setCreateConsultationInitialValues] = useState<CreateConsultationStepperValues>()

  const networkMembers = useMemo(() => _.filter(data, (item) => !!item?.member), [data])

  const availabilities = useMemo(() => {
    const availabilities: Array<{ busySlots: Slot[]; workingHours: WorkingHours[]; member: NetworkMember['member'] }> = []

    if (!networkMembers || !staffAvailabilities) return availabilities

    for (const networkMember of networkMembers) {
      const { member } = networkMember
      if (!staffAvailabilities[member._id]) continue

      availabilities.push({
        member,
        busySlots: staffAvailabilities[member._id].busySlots,
        workingHours: staffAvailabilities[member._id].workingHours,
      })
    }

    return availabilities
  }, [networkMembers, staffAvailabilities])

  const table = useReactTable({
    columns: [],
    data: availabilities,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: { pagination },
  })

  const handleSearchStaffAvailability = useCallback(async (dateFrom: Date, dateTo: Date, staff: StaffAvailabilityProps['staff']) => {
    const staffAvailability = await getStaffAvailabilities({
      dateFrom: dayjs(dateFrom).startOf('day').toISOString(),
      dateTo: dayjs(dateTo).endOf('day').toISOString(),
      owners: [staff.id],
    }).unwrap()
    setStaffAvailabilities((prevStaffAvailabilities) => _.merge({}, prevStaffAvailabilities, staffAvailability))
  }, [])

  useEffect(() => {
    if (!networkMembers?.length) return

    getStaffAvailabilities({
      dateFrom: dayjs().isoWeekday(1).startOf('day').toISOString(),
      dateTo: dayjs().isoWeekday(1).add(6, 'day').endOf('day').toISOString(),
      owners: networkMembers.filter(({ member }) => member.status === 'MEDECIN').map(({ member }) => member._id),
    })
      .unwrap()
      .then(setStaffAvailabilities)
  }, [getStaffAvailabilities, networkMembers])

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setCreateConsultationInitialValues(undefined)
  }

  if (isLoadingAvailabilities) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={100} />
        <Skeleton variant="rounded" height={100} />
        <Skeleton variant="rounded" height={100} />
      </Stack>
    )
  }

  if (!availabilities.length) {
    return <Alert severity="info">{t('page.network.schedules.noResult')}</Alert>
  }

  return (
    <>
      <Stack direction="column" spacing={2}>
        {table.getRowModel().rows.map(({ original: { busySlots, workingHours, member } }) => (
          <StaffAvailability
            key={member._id}
            busySlots={busySlots}
            calendsoAppointmentTypes={[]}
            onCalendarViewChange={handleSearchStaffAvailability}
            onChangeStartDate={(startDate, duration, _appointmentTypeId, consultationReason) => {
              setOpenDialog(true)
              setCreateConsultationInitialValues({
                medecin: member._id,
                medecinScheduleConsultationReason: consultationReason,
                startDate: startDate?.toISOString(),
                expectedDuration: duration,
              })
            }}
            workingHours={workingHours}
            staff={{
              id: member._id,
              email: member.email,
              name: member.userInfo.name,
              speciality: member.speciality,
              status: member.status,
              surname: member.userInfo.surname,
            }}
            selectable={false}
          />
        ))}
        {!!availabilities.length && (
          <Box sx={{ width: '100%', p: '10px', textAlign: 'right' }}>
            <Pagination
              color="primary"
              itemsPerPageOptions={rowsPerPageOptions}
              shape="rounded"
              page={pagination.pageIndex + 1}
              onPageChange={(page) => {
                table.setPageIndex(page - 1)
              }}
              onItemsPerPageChange={(rowsPerPage) => {
                table.setPageSize(rowsPerPage)
              }}
              totalItems={availabilities.length}
            />
          </Box>
        )}
      </Stack>
      <Dialog open={openDialog} onClose={handleCloseDialog} fullScreen TransitionComponent={Transition}>
        <AppBar position="fixed">
          <Toolbar>
            <Tooltip title={<span>{t('tooltip.button.close')}</span>}>
              <IconButton color="inherit" onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Toolbar />
        <CreateConsultationStepper
          config={{ showOnlyAvailability: true }}
          initialValues={createConsultationInitialValues}
          stepOrdinal={{
            [CreateConsultationStepEnum.SELECT_APPOINTMENT]: 0,
            [CreateConsultationStepEnum.SELECT_PATIENT]: 1,
            [CreateConsultationStepEnum.SELECT_SPECIALITY]: 2,
          }}
          onComplete={handleCloseDialog}
        />
      </Dialog>
    </>
  )
}
